import React, { useContext, useState } from "react";
import useSpacing from '../../core/hooks/useSpacing';
import useBaseStyle from '../../core/hooks/useBaseStyle';
import useContainerStyle from '../../core/hooks/useContainerStyle';
import { EditContext } from "../../editor/context/EditContext";
import { getPositionStyles, calculateWidth } from "../utils/mediaUtils";
import { getFileDisplayName, formatFileSize, supportsPreview, stripQueryString } from "../../core/utils/fileUtils";
import FilePreview from "./FilePreview";
import FileEmbedContent from "./FileEmbedContent";

// File type icons mapping - using simple SVG icons
const FILE_TYPE_ICONS = {
  pdf: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10,9 9,9 8,9" />
    </svg>
  ),
  doc: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10,9 9,9 8,9" />
    </svg>
  ),
  docx: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10,9 9,9 8,9" />
    </svg>
  ),
  xls: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  ),
  xlsx: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  ),
  ppt: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <circle cx="8" cy="14" r="2" />
      <path d="M10 12h4" />
    </svg>
  ),
  pptx: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <circle cx="8" cy="14" r="2" />
      <path d="M10 12h4" />
    </svg>
  ),
  txt: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  rtf: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  csv: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  ),
};

const FILE_TYPE_COLORS = {
  pdf: "text-red-600",
  doc: "text-blue-600",
  docx: "text-blue-600",
  xls: "text-green-600", 
  xlsx: "text-green-600",
  ppt: "text-orange-600",
  pptx: "text-orange-600",
  txt: "text-gray-600",
  rtf: "text-gray-600",
  csv: "text-green-600",
};

const FileItem = ({ item }) => {
  const { combinedClasses } = useSpacing(item);
  const { styleClasses, inlineStyles } = useBaseStyle(item);
  const containerClasses = useContainerStyle(item);
  const editContext = useContext(EditContext);
  const isEditMode = !!editContext;
  const [showPreview, setShowPreview] = useState(false);

  // Handle null/undefined items gracefully
  if (!item) {
    return null;
  }

  // If no file URL, show a placeholder only in edit mode, hide completely in published mode
  if (!item.file_url || (typeof item.file_url === 'string' && item.file_url.trim() === '')) {
    // In published mode, hide the component completely if no file
    if (!isEditMode) {
      return null;
    }
    
    // In edit mode, show clickable placeholder
    const finalClasses = `${combinedClasses} ${styleClasses} ${containerClasses}`.trim();
    
    return (
      <div 
        className={`${finalClasses} min-h-[120px] border-2 border-dashed border-gray-400 bg-gray-100 hover:bg-gray-200 hover:border-gray-500 transition-all duration-200 flex flex-col items-center justify-center text-gray-600 rounded-lg cursor-pointer`}
        style={{
          ...inlineStyles,
          minHeight: '120px',
          minWidth: '200px'
        }}
        title="Click to add a file"
      >
        <div className="text-center p-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-10 h-10 mx-auto mb-2 text-gray-500 block"
          >
            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          </svg>
          <div className="text-sm font-medium">No File Selected</div>
          <div className="text-xs mt-1 opacity-75">Click to add a file</div>
        </div>
      </div>
    );
  }

  const finalClasses = `${combinedClasses} ${styleClasses} ${containerClasses}`.trim();
  const fileUrl = item.file_url;

  const positionStyles = getPositionStyles(item.position);

  const containerStyle = {
    display: "flex",
    ...positionStyles,
    height: "100%",
  };

  // Extract file extension for icon and color
  const getFileExtension = () => {
    if (item.file_type) {
      return item.file_type.toLowerCase();
    }
    // Strip presigned query params before splitting so we get a clean extension
    const source = item.file_s3_key || (item.file_url ? stripQueryString(item.file_url) : '');
    const filename = item.file_name || (source ? source.split('/').pop() : '');
    if (filename && filename.includes('.')) {
      return filename.split('.').pop().toLowerCase();
    }
    return "file";
  };

  const fileExtension = getFileExtension();
  const fileIcon = FILE_TYPE_ICONS[fileExtension] || FILE_TYPE_ICONS.txt;


  const getFileStyle = () => {
    const fileStyle = { maxWidth: "100%" };

    // Handle width
    // Card mode should be determined by max width (legacy fields).
    // Embed mode can use the new `size` slider (fallback to legacy max width).
    const widthSource =
      item.display_mode === "embed" ? item : { ...item, size: null };
    const finalWidth = calculateWidth(widthSource);
    if (finalWidth != null) {
      if (
        widthSource.max_width_pct_component != null &&
        finalWidth === widthSource.max_width_pct_component
      ) {
        fileStyle.maxWidth = `${finalWidth}%`;
      } else if (widthSource.size != null && finalWidth === widthSource.size) {
        fileStyle.maxWidth = `${finalWidth}%`;
      } else {
        fileStyle.maxWidth = finalWidth;
      }
    }

    return fileStyle;
  };

  const fileStyle = getFileStyle();
  const mergedInlineStyles = {
    ...fileStyle,
    ...inlineStyles,
  };

  const displayName = getFileDisplayName(item);
  const downloadName = item.download_name || displayName;
  const fileSize = formatFileSize(item.file_size);
  const canPreview = supportsPreview(fileExtension);

  const handleFileClick = (e) => {
    // In editor mode, clicking should select the element (EditableElement) and open EditPanel,
    // not navigate / preview / download.
    if (isEditMode) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    
    if (canPreview) {
      setShowPreview(true);
    } else {
      handleDownload();
    }
  };

  const handleDownload = () => {
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = downloadName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Inline embed mode
  if (item.display_mode === 'embed') {
    const embedHeight = item.embed_height || 600;
    return (
      <div style={containerStyle}>
        <div
          className={`${finalClasses} bg-white border border-gray-200 rounded-lg overflow-hidden`}
          style={{ ...mergedInlineStyles, width: '100%' }}
        >
          <FileEmbedContent file={item} height={embedHeight} />
        </div>
      </div>
    );
  }

  // Default card mode
  return (
    <div style={containerStyle}>
      <div
        className={`${finalClasses} bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 cursor-pointer`}
        style={mergedInlineStyles}
        onClick={handleFileClick}
        title={canPreview ? `Preview ${displayName}` : `Download ${displayName}`}
      >
        <div className="flex items-start space-x-3">
          <div className="text-gray-600 flex-shrink-0">
            {fileIcon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 truncate">
              {displayName}
            </div>
            {item.description && (
              <div className="text-sm text-gray-600 mt-1 line-clamp-2">
                {item.description}
              </div>
            )}
            <div className="flex items-center space-x-2 mt-2 text-xs text-gray-500">
              <span className="uppercase font-medium">{fileExtension}</span>
              {fileSize && (
                <>
                  <span>•</span>
                  <span>{fileSize}</span>
                </>
              )}
            </div>
          </div>
          <div className="text-gray-400 hover:text-gray-600 flex-shrink-0">
            {canPreview ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            )}
          </div>
        </div>
      </div>
      {showPreview && (
        <FilePreview file={item} isOpen={showPreview} onClose={() => setShowPreview(false)} />
      )}
    </div>
  );
};

export default FileItem;
